// Web-push client helpers (Jaime-approved, 2026-09-17).
// Registers the service worker, requests Notification permission, subscribes
// with the backend's VAPID public key (VITE_PUBLIC_VAPID_KEY) and POSTs the
// subscription to /api/push/register.

import api from '../services/api';

const VAPID_PUBLIC_KEY = (import.meta.env.VITE_PUBLIC_VAPID_KEY as string) || '';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js');
  } catch (err) {
    console.warn('[push] service worker registration failed:', err);
    return null;
  }
}

export type PushEnableResult =
  | { ok: true }
  | { ok: false; reason: 'unsupported' | 'no-key' | 'denied' | 'sw' | 'subscribe' | 'register' };

export async function enablePushNotifications(): Promise<PushEnableResult> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { ok: false, reason: 'unsupported' };
  }
  if (!VAPID_PUBLIC_KEY) return { ok: false, reason: 'no-key' };

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: 'denied' };

  const registration = await registerServiceWorker();
  if (!registration) return { ok: false, reason: 'sw' };

  let sub: PushSubscription;
  try {
    const existing = await registration.pushManager.getSubscription();
    sub = existing || (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as unknown as BufferSource,
    }));
  } catch (err) {
    console.warn('[push] subscribe failed:', err);
    return { ok: false, reason: 'subscribe' };
  }

  try {
    await api.post('/push/register', sub.toJSON());
    return { ok: true };
  } catch (err) {
    console.warn('[push] register call failed:', err);
    return { ok: false, reason: 'register' };
  }
}

// Silent push re-association (Jaime product ruling 2026-10-19 21:11):
// push notifications are preset as approved on our side. When a user logs in
// (or the app boots with a valid session), silently associate this browser's
// existing web-push registration with the logged-in account — no prompt, no
// setup page visit. Fire-and-forget and non-fatal: never blocks login, never
// error-toasts. If permission is NOT granted, do nothing (the opt-in prompt
// flow in PushNotificationSetup / Perfil stays the only path to ask).
// Reuses the same POST /push/register contract as enablePushNotifications;
// the backend upserts one row per endpoint, so re-POSTing under the new
// account's token re-associates the registration (per-user upsert semantics).
export async function silentlyAssociatePush(): Promise<void> {
  try {
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    if (typeof Notification === 'undefined') return;
    if (!VAPID_PUBLIC_KEY) return;
    if (Notification.permission !== 'granted') return; // granted-only path

    // Reuse the same SW registration the opt-in flow created; register only
    // if it is missing (silent — permission already granted, no prompt).
    const registration = await registerServiceWorker();
    if (!registration) return;
    let sub: PushSubscription | null = null;
    try {
      sub = await registration.pushManager.getSubscription();
      if (!sub) {
        sub = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as unknown as BufferSource,
        });
      }
    } catch (err) {
      console.warn('[push] silent subscribe failed:', err);
      return;
    }
    if (!sub) return;
    await api.post('/push/register', sub.toJSON());
  } catch (err) {
    console.warn('[push] silent re-association failed:', err);
  }
}

export function pushPromptShown(): boolean {
  try { return localStorage.getItem('vecindus_push_prompt_shown') === '1'; } catch { return true; }
}

export function markPushPromptShown(): void {
  try { localStorage.setItem('vecindus_push_prompt_shown', '1'); } catch { /* ignore */ }
}
